import type { FlowDocument, FlowNodePosition } from "./types";

/**
 * Commits one node's dragged-to position, and — critically — bakes in
 * every OTHER node's currently-displayed position at the same time.
 *
 * Why bake in every node, not just the one dragged: layoutFlow only
 * respects a document's saved positions when EVERY node has one (see
 * layout.ts). If dragging wrote a position onto just the one node the
 * user moved, the very next render would find "not every node has a
 * position" still true, fall back to full auto-layout again, and silently
 * discard the drag — the layout would visually snap back, which is
 * indistinguishable from dragging "not working" at all. Freezing the
 * whole current layout the first time any node is dragged is what makes a
 * drag stick: `currentPositions` is whatever layoutFlow just computed
 * (auto-layout or previously-saved, either way), so this is the intended
 * one-way transition from "auto-laid-out" to "has a saved layout" a real
 * designer's drag should cause.
 */
export function commitNodeDrag(
  doc: FlowDocument,
  currentPositions: Record<string, FlowNodePosition>,
  draggedNodeId: string,
  newPosition: FlowNodePosition,
): FlowDocument {
  return {
    ...doc,
    nodes: doc.nodes.map((node) => ({
      ...node,
      position:
        node.id === draggedNodeId
          ? newPosition
          : (currentPositions[node.id] ?? node.position ?? { x: 0, y: 0 }),
    })),
  };
}
